import React, { useState } from 'react';
import { Button, TextInput, Textarea, Modal } from '@mantine/core';
import apiService from '../../services/api';
import { LandingPageProps } from '../../types';

interface ContactFormData {
  username: string;
  subject: string;
  summary: string;
}

interface ContactMessage {
  type: string;
  text: string;
}

interface Feature {
  icon: string;
  title: string;
  description: string;
}

const LandingPage: React.FC<LandingPageProps> = ({ onNavigateToClient, onNavigateToTrainer, onNavigateToAdmin }) => {
  const [showDisclosure, setShowDisclosure] = useState<boolean>(false);
  const [disclosureType, setDisclosureType] = useState<string>('');
  const [contactForm, setContactForm] = useState<ContactFormData>({
    username: '',
    subject: '',
    summary: ''
  });
  const [contactSubmitting, setContactSubmitting] = useState<boolean>(false);
  const [contactMessage, setContactMessage] = useState<ContactMessage>({ type: '', text: '' });

  const handlePortalAccess = (type: string) => {
    setDisclosureType(type);
    setShowDisclosure(true);
  };

  const handleContactChange = (field: string, value: string) => {
    setContactForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleContactSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const trimmedUsername = contactForm.username.trim();
    const trimmedSubject = contactForm.subject.trim();
    const trimmedSummary = contactForm.summary.trim();

    if (!trimmedUsername || !trimmedSubject || !trimmedSummary) {
      setContactMessage({ type: 'error', text: 'Please complete all fields before submitting.' });
      return;
    }

    if (trimmedSubject.length > 200) {
      setContactMessage({ type: 'error', text: 'Subject must be 200 characters or fewer.' });
      return;
    }

    setContactSubmitting(true);
    setContactMessage({ type: '', text: '' });

    try {
      await apiService.createSupportTicket({
        username: trimmedUsername,
        subject: trimmedSubject,
        summary: trimmedSummary
      });

      setContactMessage({ type: 'success', text: 'Thanks for reaching out! Your ticket has been submitted.' });
      setContactForm({ username: '', subject: '', summary: '' });
    } catch (error) {
      setContactMessage({
        type: 'error',
        text: (error as Error).message || 'We were unable to submit your ticket. Please try again.'
      });
    } finally {
      setContactSubmitting(false);
    }
  };

  const handleAcceptDisclosure = () => {
    setShowDisclosure(false);
    switch (disclosureType) {
      case 'client':
        onNavigateToClient();
        break;
      case 'trainer':
        onNavigateToTrainer();
        break;
      case 'admin':
        onNavigateToAdmin();
        break;
      default:
        break;
    }
  };

  const features: Feature[] = [
    {
      icon: '',
      title: 'Expert Trainers',
      description: 'Connect with experienced dog trainers who specialize in your specific needs.'
    },
    {
      icon: '',
      title: 'Virtual Sessions',
      description: 'Get professional training from the comfort of your home through secure video calls.'
    },
    {
      icon: '',
      title: 'Easy Access',
      description: 'Simple booking, session management, and progress tracking all in one platform.'
    },
    {
      icon: '',
      title: 'Progress Tracking',
      description: 'Monitor your dog\'s training progress with detailed summaries and actionable checklists.'
    },
    {
      icon: '',
      title: 'Secure & Safe',
      description: 'Your data and sessions are protected with enterprise-grade security measures.'
    },
    {
      icon: '',
      title: 'Transparent Pricing',
      description: 'Flexible pricing with transparent rates and no hidden fees.'
    }
  ];

  const disclosureText: Record<string, string> = {
    client: `By accessing the Client Portal, you agree to the following terms and conditions:

1. **Service Agreement**: You understand that Pheefee provides virtual dog training services through trainers.

2. **Payment Terms**: Sessions are billed at the trainer's published rates. The first 10 minutes are charged at a fixed rate, with additional time charged per minute.

3. **Session Recording**: Sessions may be recorded for quality assurance and training purposes. You will be notified before recording begins.

4. **Liability**: While our trainers are professionals, you understand that dog training involves inherent risks and agree to hold Pheefee harmless for any incidents.

5. **Data Privacy**: Your personal information and session data will be protected according to our privacy policy.

6. **Cancellation Policy**: Cancellations must be made at least 2 hours before your scheduled session to avoid charges.

Do you agree to these terms and wish to proceed to the Client Portal?`,

    trainer: `By accessing the Trainer Portal, you agree to the following terms and conditions:

1. **Independent Contractor Status**: You understand that you are an independent contractor, not an employee of Pheefee.

2. **Professional Standards**: You agree to maintain the highest professional standards and follow all applicable laws and regulations.

3. **Payment Terms**: You will receive payment on the first Monday of each month for completed sessions, minus platform fees.

4. **Background Check**: You consent to background checks and agree to provide all required documentation.

5. **Insurance Requirements**: You must maintain appropriate liability insurance and provide proof of coverage.

6. **Platform Rules**: You agree to follow all platform guidelines and maintain professional conduct during sessions.

7. **Data Security**: You will protect client information and maintain confidentiality at all times.

Do you agree to these terms and wish to proceed to the Trainer Portal?`,

    admin: `By accessing the Admin Portal, you agree to the following terms and conditions:

1. **Administrative Access**: You are authorized to access administrative functions and sensitive data.

2. **Data Security**: You will maintain the highest level of security and confidentiality for all platform data.

3. **Compliance**: You will ensure all operations comply with applicable laws and regulations.

4. **Audit Trail**: All administrative actions will be logged and monitored.

5. **Access Control**: You will not share administrative credentials or access with unauthorized personnel.

Do you agree to these terms and wish to proceed to the Admin Portal?`
  };

  return (
    <div className="font-sans bg-[#0f172a] min-h-screen text-[#f1f5f9] flex flex-col">
      {/* Header */}
      <header className="bg-[#1e293b] py-5 shadow-[0_1px_0_rgba(0,0,0,0.2)] border-b border-[#334155]">
        <div className="max-w-[1200px] mx-auto px-5 flex items-center justify-between">
          <div className="flex items-center gap-4 cursor-pointer">
            <img
              src="/images/logo.png"
              alt="Pheefee Logo"
              className="w-[50px] h-[50px] rounded-xl object-contain"
            />
            <span className="text-2xl sm:text-3xl font-bold text-[#f1f5f9]">Pheefee</span>
          </div>
          <nav className="hidden md:flex gap-8 items-center">
            <a href="#features" className="text-[#cbd5e1] no-underline text-lg font-medium px-5 py-2.5 rounded-[25px] bg-[#334155] border border-[#475569] hover:text-white transition-all">Features</a>
            <a href="#about" className="text-[#cbd5e1] no-underline text-lg font-medium px-5 py-2.5 rounded-[25px] bg-[#334155] border border-[#475569] hover:text-white transition-all">About</a>
            <a href="#contact" className="text-[#cbd5e1] no-underline text-lg font-medium px-5 py-2.5 rounded-[25px] bg-[#334155] border border-[#475569] hover:text-white transition-all">Contact</a>
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center justify-center px-4 sm:px-5 py-10 sm:py-16 text-center">
        <h1 className="text-3xl sm:text-5xl md:text-6xl font-extrabold mb-5 text-[#f1f5f9] leading-tight">
          Professional Dog Training<br />
          <span className="text-2xl sm:text-4xl md:text-5xl">On Demand</span>
        </h1>
        <p className="text-lg sm:text-xl md:text-2xl mb-10 text-[#cbd5e1] max-w-[600px] leading-relaxed">
          Connect with dog trainers for personalized virtual training sessions.
          Get expert guidance, track progress, and achieve your training goals from anywhere.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-[900px] w-full mt-10">
          <div
            className="bg-[#1e293b] backdrop-blur-sm rounded-2xl py-6 px-5 md:py-10 md:px-8 text-center border border-[#334155] transition-all cursor-pointer relative overflow-hidden hover:-translate-y-1 hover:shadow-[0_8px_16px_rgba(0,0,0,0.3)] hover:border-[#475569]"
            onClick={() => handlePortalAccess('client')}
          >
            <span className="text-4xl md:text-6xl mb-5 block">Client</span>
            <h2 className="text-2xl md:text-3xl font-bold mb-4 text-[#f1f5f9]">Client Portal</h2>
            <p className="text-base text-[#cbd5e1] leading-relaxed mb-6">
              Book training sessions, track your dog's progress, access session summaries,
              and manage your training journey with professional guidance.
            </p>
            <Button
              fullWidth
              radius="xl"
              size="lg"
              className="bg-[#3b82f6] shadow-[0_4px_15px_rgba(59,130,246,0.3)]"
            >
              Access Client Portal
            </Button>
          </div>

          <div
            className="bg-[#1e293b] backdrop-blur-sm rounded-2xl py-6 px-5 md:py-10 md:px-8 text-center border border-[#334155] transition-all cursor-pointer relative overflow-hidden hover:-translate-y-1 hover:shadow-[0_8px_16px_rgba(0,0,0,0.3)] hover:border-[#475569]"
            onClick={() => handlePortalAccess('trainer')}
          >
            <span className="text-4xl md:text-6xl mb-5 block">Trainer</span>
            <h2 className="text-2xl md:text-3xl font-bold mb-4 text-[#f1f5f9]">Trainer Portal</h2>
            <p className="text-base text-[#cbd5e1] leading-relaxed mb-6">
              Manage your training business, connect with clients, conduct virtual sessions,
              and grow your professional practice with our platform.
            </p>
            <Button
              fullWidth
              radius="xl"
              size="lg"
              className="bg-[#3b82f6] shadow-[0_4px_15px_rgba(59,130,246,0.3)]"
            >
              Access Trainer Portal
            </Button>
          </div>
        </div>
      </main>

      {/* About Section */}
      <section id="about" className="bg-[#0f172a] py-10 sm:py-16 md:py-20 px-4 sm:px-5 border-t border-b border-[#1e293b]">
        <div className="max-w-[1100px] mx-auto grid grid-cols-1 md:grid-cols-[repeat(auto-fit,minmax(280px,1fr))] gap-10 items-center">
          <div className="text-left">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-5 text-[#f39c12]">About Pheefee</h2>
            <p className="text-base text-[#cbd5e1] leading-relaxed mb-4">
              Pheefee pairs pet guardians with trainers who are passionate about helping
              dogs thrive. We blend proven training methodologies with real-time virtual coaching so you
              can build consistent habits that fit your lifestyle.
            </p>
            <p className="text-base text-[#cbd5e1] leading-relaxed mb-4">
              From puppy socialization to advanced behavior modification, our trainers collaborate with you
              to create actionable plans, track progress, and celebrate every milestone along the journey.
              Training should feel empowering, and we make sure you have the tools to succeed long after
              each session ends.
            </p>
            <div className="grid gap-4 mt-5">
              <div className="bg-blue-500/10 border border-blue-500/25 rounded-xl px-5 py-4 text-[#93c5fd] text-sm leading-relaxed">Trainers are screened for specialty experience</div>
              <div className="bg-blue-500/10 border border-blue-500/25 rounded-xl px-5 py-4 text-[#93c5fd] text-sm leading-relaxed">Structured session recaps and personalized task lists</div>
              <div className="bg-blue-500/10 border border-blue-500/25 rounded-xl px-5 py-4 text-[#93c5fd] text-sm leading-relaxed">Secure video platform with integrated payments and scheduling</div>
            </div>
          </div>
          <div className="bg-[#1e293b] rounded-2xl p-8 border border-[#334155] shadow-[0_15px_35px_rgba(15,23,42,0.45)] grid gap-5">
            <div className="text-left">
              <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#38bdf8]">15+</div>
              <div className="text-base text-[#cbd5e1] mt-1.5">Specialty training areas covered</div>
            </div>
            <div className="text-left">
              <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#38bdf8]">98%</div>
              <div className="text-base text-[#cbd5e1] mt-1.5">Client satisfaction score across recent sessions</div>
            </div>
            <div className="text-left">
              <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#38bdf8]">24/7</div>
              <div className="text-base text-[#cbd5e1] mt-1.5">Flexible scheduling to match your routine</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="bg-[#1e293b] py-10 sm:py-16 md:py-20 px-4 sm:px-5 mt-0">
        <div className="max-w-[1200px] mx-auto">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-12 text-[#f39c12]">Why Choose Pheefee?</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[repeat(auto-fit,minmax(250px,1fr))] gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-white/10 rounded-2xl p-8 text-center border border-white/20">
                <span className="text-5xl mb-5 block text-[#f39c12]">{feature.icon}</span>
                <h3 className="text-xl font-semibold mb-4 text-white">{feature.title}</h3>
                <p className="text-base text-white/80 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="bg-[#0b1120] py-10 sm:py-16 md:py-20 px-4 sm:px-5">
        <div className="max-w-[900px] mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 text-[#38bdf8]">Contact Us</h2>
          <p className="text-lg text-[#cbd5e1] mb-9 leading-relaxed">
            Have a question, feature request, or need help with your account? Send us a ticket and our team
            will get back to you within one business day.
          </p>
          <form className="bg-[#1e293b] rounded-2xl p-5 sm:p-7 md:p-9 border border-[#334155] shadow-[0_10px_30px_rgba(15,23,42,0.45)] text-left" onSubmit={handleContactSubmit}>
            <div className="mb-5">
              <label className="block text-[#e2e8f0] mb-2 font-semibold">
                Username
              </label>
              <input
                className="w-full p-3 rounded-xl border border-[#334155] bg-[#0f172a] text-[#f8fafc] text-base"
                type="text"
                value={contactForm.username}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleContactChange('username', e.target.value)}
                placeholder="Enter the username tied to your account"
              />
            </div>
            <div className="mb-5">
              <label className="block text-[#e2e8f0] mb-2 font-semibold">
                Subject
              </label>
              <input
                className="w-full p-3 rounded-xl border border-[#334155] bg-[#0f172a] text-[#f8fafc] text-base"
                type="text"
                value={contactForm.subject}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleContactChange('subject', e.target.value)}
                placeholder="How can we help?"
                maxLength={200}
              />
            </div>
            <div>
              <label className="block text-[#e2e8f0] mb-2 font-semibold">
                Issue Summary
              </label>
              <textarea
                className="w-full min-h-[160px] p-3.5 rounded-xl border border-[#334155] bg-[#0f172a] text-[#f8fafc] text-base resize-y mb-6 leading-relaxed"
                value={contactForm.summary}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleContactChange('summary', e.target.value)}
                placeholder="Share details about the issue or question you're experiencing..."
              />
            </div>
            <Button
              type="submit"
              fullWidth
              loading={contactSubmitting}
              radius="xl"
              size="lg"
              className="bg-gradient-to-br from-[#38bdf8] to-[#2563eb] shadow-[0_12px_25px_rgba(37,99,235,0.35)]"
            >
              Submit Support Ticket
            </Button>
            {contactMessage.text && (
              <div className={`mt-4 text-sm font-medium ${contactMessage.type === 'success' ? 'text-emerald-400' : 'text-red-400'}`}>
                {contactMessage.text}
              </div>
            )}
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black/20 py-10 px-5 text-center text-white/80">
        <p>&copy; 2024 Pheefee. All rights reserved. | Privacy Policy | Terms of Service</p>
      </footer>

      {/* Disclosure Modal */}
      <Modal
        opened={showDisclosure}
        onClose={() => setShowDisclosure(false)}
        title={
          disclosureType === 'client' ? 'Client Portal Agreement' :
          disclosureType === 'trainer' ? 'Trainer Portal Agreement' :
          'Admin Portal Agreement'
        }
        centered
        radius="lg"
        size="lg"
      >
        <div className="text-base leading-relaxed mb-8 text-gray-600 whitespace-pre-line">
          {disclosureText[disclosureType]}
        </div>
        <div className="flex gap-4 justify-center">
          <Button
            onClick={handleAcceptDisclosure}
            radius="md"
            size="lg"
            className="bg-gradient-to-r from-[#e67e22] to-[#f39c12]"
          >
            I Agree - Continue
          </Button>
          <Button
            onClick={() => setShowDisclosure(false)}
            variant="outline"
            color="red"
            radius="md"
            size="lg"
          >
            Cancel
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default LandingPage;
